import { useState, useEffect } from 'react';
import { sendChartCommand, getWebSocketCommandsHealth, getActiveDashboards } from '../api/websocketCommands';

function CommandsPage() {
  const [lastCommand, setLastCommand] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState('unknown');
  const [targetDashboard, setTargetDashboard] = useState('all');
  const [activeDashboards, setActiveDashboards] = useState([]);
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [customWebsocketId, setCustomWebsocketId] = useState('');

  // Check API health and load dashboards on component mount
  useEffect(() => {
    checkApiHealth();
    loadActiveDashboards();
  }, []);

  const checkApiHealth = async () => {
    try {
      await getWebSocketCommandsHealth();
      setApiStatus('healthy');
    } catch (error) {
      setApiStatus('error');
    }
  };

  const loadActiveDashboards = async () => {
    setDashboardsLoading(true);
    try {
      const response = await getActiveDashboards();
      setActiveDashboards(response.dashboards || []);
    } catch {
      setActiveDashboards([]);
    } finally {
      setDashboardsLoading(false);
    }
  };

  const sendCommand = async (command) => {
    setIsLoading(true);
    try {
      let userId, websocketId;
      
      if (targetDashboard === 'all') {
        // For broadcast to all, we need to handle this differently
        // For now, let's use the first available dashboard or skip
        alert('Broadcasting to all dashboards is not yet supported. Please select a specific dashboard.');
        return;
      } else if (targetDashboard === 'custom') {
        // For custom websocketId, we need to find the userId
        // This is a limitation - we need both userId and websocketId
        alert('Custom WebSocket ID is not yet supported. Please select from the list of active dashboards.');
        return;
      } else {
        // Find the selected dashboard to get both userId and websocketId
        const selectedDashboard = activeDashboards.find(d => d.websocketId === targetDashboard);
        if (!selectedDashboard) {
          alert('Selected dashboard not found. Please refresh and try again.');
          return;
        }
        userId = selectedDashboard.userId;
        websocketId = selectedDashboard.websocketId;
      }
      
      const response = await sendChartCommand(command, userId, websocketId, 'global');
      setLastCommand(command);
    } catch {
      alert('Failed to send command. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const testCommands = [
    {
      name: 'Scale Main Chart to 50%',
      command: {
        elementId: 'mainCanvas',
        action: 'scale',
        dimensions: { width: '33.34%', height: 'calc((100vh - 400px) * 0.75)' },
        position: { top: 'calc(312px + 20vh)', left: '32px' },
        zIndex: 10
      }
    },
    {
      name: 'Reset Main Chart to 100%',
      command: {
        elementId: 'mainCanvas',
        action: 'reset',
        dimensions: { width: '66.67%', height: 'calc(100vh - 400px)' },
        position: { top: '312px', left: '32px' },
        zIndex: 10
      }
    },
    {
      name: 'Move Chart to Right Side',
      command: {
        elementId: 'mainCanvas',
        action: 'move',
        dimensions: { width: '33.34%', height: 'calc((100vh - 400px) * 0.5)' },
        position: { top: '312px', left: 'calc(66.67% + 32px)' },
        zIndex: 15
      }
    },
    {
      name: 'Move Chart to Center',
      command: {
        elementId: 'mainCanvas',
        action: 'center',
        dimensions: { width: '40%', height: '60%' },
        position: { top: '20%', left: '30%' },
        zIndex: 20
      }
    },
    {
      name: 'Minimize Chart (Bottom Right)',
      command: {
        elementId: 'mainCanvas',
        action: 'minimize',
        dimensions: { width: '20%', height: '25%' },
        position: { top: 'calc(100vh - 200px)', left: 'calc(100vw - 320px)' },
        zIndex: 30
      }
    },
    {
      name: 'Scale Side Panel to 75%',
      command: {
        elementId: 'sidePanel',
        action: 'scale',
        dimensions: { width: 'calc(25% - 88px)', height: 'calc((100vh - 400px) * 0.75)' },
        position: { top: '312px', left: 'calc(75% + 56px)' },
        zIndex: 10
      }
    },
    {
      name: 'Scale Side Panel back to 100%',
      command: {
        elementId: 'sidePanel',
        action: 'reset',
        dimensions: { width: 'calc(33.33% - 88px)', height: 'calc(100vh - 400px)' },
        position: { top: '312px', left: 'calc(66.67% + 56px)' },
        zIndex: 10
      }
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Chart Commands</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Send test commands to control chart positioning and scaling via WebSocket.
        </p>
      </div>

      {/* API Status */}
      <div className="mb-6 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              apiStatus === 'healthy' ? 'bg-green-500' :
              apiStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
            }`}></div>
            <span className={`font-medium ${
              apiStatus === 'healthy' ? 'text-green-700 dark:text-green-300' :
              apiStatus === 'error' ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'
            }`}>
              WebSocket API {
                apiStatus === 'healthy' ? 'Ready' :
                apiStatus === 'error' ? 'Error' : 'Checking...'
              }
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={checkApiHealth}
              disabled={isLoading}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
            >
              Refresh API
            </button>
            <button
              onClick={loadActiveDashboards}
              disabled={dashboardsLoading}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
            >
              {dashboardsLoading ? 'Loading...' : 'Refresh Dashboards'}
            </button>
          </div>
        </div>
        {apiStatus === 'error' && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            WebSocket broadcasting service unavailable. Check backend connection.
          </p>
        )}
        {apiStatus === 'healthy' && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Commands will be sent via HTTP API and broadcasted to all connected WebSocket clients.
          </p>
        )}
      </div>

      {/* Dashboard Targeting */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Target Dashboard</h2>

        {/* Active Dashboards Summary */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${activeDashboards.filter(d => d.status === 'connected').length > 0 ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {activeDashboards.filter(d => d.status === 'connected').length} of {activeDashboards.length} dashboards connected
              </span>
            </div>
            {dashboardsLoading && (
              <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600 rounded-full animate-spin"></div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Send commands to:</label>
          <select
            value={targetDashboard}
            onChange={(e) => {
              setTargetDashboard(e.target.value);
              if (e.target.value !== 'custom') {
                setCustomWebsocketId('');
              }
            }}
            className="px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-obn-blue dark:focus:ring-blue-400 focus:border-transparent dark:focus:border-blue-400"
          >
            <option value="all">All Dashboards (Broadcast)</option>
            {activeDashboards.length > 0 && (
              <>
                <option value="" disabled>─────────────────────</option>
                {activeDashboards.map((dashboard) => (
                  <option key={dashboard.websocketId} value={dashboard.websocketId}>
                    Dashboard #{dashboard.websocketId}
                    {dashboard.status === 'connected' ? ' 🟢' : ' 🔴'}
                    {dashboard.isNew ? ' (New)' : ''}
                  </option>
                ))}
              </>
            )}
            <option value="" disabled>─────────────────────</option>
            <option value="custom">Enter WebSocket ID...</option>
          </select>

          {targetDashboard === 'custom' && (
            <input
              type="text"
              placeholder="Enter 5-digit ID"
              value={customWebsocketId}
              onChange={(e) => setCustomWebsocketId(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 border border-gray-300 dark:border-gray-600 rounded-md text-sm w-32 focus:outline-none focus:ring-2 focus:ring-obn-blue dark:focus:ring-blue-400 focus:border-transparent dark:focus:border-blue-400"
              pattern="[0-9]{5}"
              maxLength={5}
            />
          )}
        </div>

        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {targetDashboard === 'all'
            ? 'Commands will be sent to all connected dashboards globally (not yet supported)'
            : targetDashboard === 'custom'
            ? `Commands will be sent to dashboard #${customWebsocketId || '[enter ID]'} (not yet supported)`
            : `Commands will be sent only to dashboard #${targetDashboard}`
          }
        </div>

        {/* Dashboard Details */}
        {activeDashboards.length > 0 && (
          <div className="mt-4">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">
                View Dashboard Details ({activeDashboards.length})
              </summary>
              <div className="mt-2 space-y-2">
                {activeDashboards.map((dashboard) => (
                  <div key={dashboard.websocketId} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${dashboard.status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="font-mono font-medium text-gray-900 dark:text-gray-100">#{dashboard.websocketId}</span>
                      <span className="text-gray-500 dark:text-gray-400">User: {dashboard.userId.substring(0, 8)}...</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-500 dark:text-gray-400">
                      <span>Age: {Math.round(dashboard.age / 1000)}s</span>
                      <span className="capitalize">{dashboard.status}</span>
                      {dashboard.isNew && <span className="text-blue-600 dark:text-blue-400">New</span>}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Command Buttons */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Test Commands</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testCommands.map((testCommand, index) => (
            <button
              key={index}
              onClick={() => sendCommand(testCommand.command)}
              disabled={apiStatus !== 'healthy' || isLoading}
              className="p-4 bg-obn-blue text-white rounded-lg hover:bg-obn-blue/90 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors text-left relative"
            >
              {isLoading && (
                <div className="absolute inset-0 bg-obn-blue/50 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              <div className="font-medium mb-2">{testCommand.name}</div>
              <div className="text-sm text-obn-lightblue">
                Action: {testCommand.command.action} | Element: {testCommand.command.elementId}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Command Format Documentation */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Command Format</h2>
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <pre className="text-sm text-gray-700 dark:text-gray-200 overflow-x-auto">
{`{
  "type": "chart_command",
  "command": {
    "elementId": "mainCanvas",
    "action": "scale|move|reset|center|minimize",
    "dimensions": {
      "width": "33.34%",
      "height": "calc((100vh - 400px) * 0.5)"
    },
    "position": {
      "top": "312px",
      "left": "32px"
    },
    "zIndex": 10
  },
  "timestamp": "2024-01-01T00:00:00.000Z"
}`}
          </pre>
        </div>
      </div>

      {/* Last Command Sent */}
      {lastCommand && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Last Command Sent</h2>
          <div className="bg-obn-lightblue/20 dark:bg-blue-900/30 p-4 rounded-lg">
            <pre className="text-sm text-gray-700 dark:text-gray-200 overflow-x-auto">
              {JSON.stringify(lastCommand, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommandsPage;